import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { API_RESPONSES } from 'types/baseTypes';
import { useAppSelector } from 'hooks';
import SidePanel from 'components/side-panel/SidePanel';

import {
  useDeleteJobMutation,
  useDeleteNoteMutation,
  useEditJobAssigneesMutation,
  useEditJobLeadSourceMutation,
  useEditJobScheduleMutation,
  useLazyGetAssignableMembersQuery,
  useLazyGetJobByIdQuery,
  useLazyGetJobTimelineQuery
} from '../api';
import { AssignableMember, Assignee, Job, JobTimelineEvent } from '../jobs.model';
import AssignableMembersModal from '../create-edit-job/components/AssignableMembersModal';
import JobDetailsPanel from './JobDetailsPanel';
import { selectCurrentTeam, selectIsActionsBlocked } from 'containers/app/appSlice';
import { SidePanelRouteState } from 'routes/types';
import EditStatusPanel from './EditStatusPanel';
import { JobStatus } from '../constant';
import EditStatusDetailPanel from './EditStatusDetailPanel';
import EditDescriptionPanel from './EditDesctiptionPanel';
import EditLeadSourcePanel from './EditLeadSourcePanel';
import EditScheduleModal from './EditScheduleModal';
import JobHistoryPanel from './JobHistoryPanel';
import DateUtils from 'utils/dateUtils';
import CreateEditNoteModal from './CreateEditNoteModal';
import { DeleteNoteConfirmationModal } from './DeleteNoteConfirmationModal';
import { ImagePreview } from 'components';
import DoneStatusSuccessModal from './DoneStatusSuccessModal';
import { DeleteJobConfirmationModal } from './DeleteJobConfrimationModal';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../events';
import MerchantOrTeamBlockedInfoPanel from 'components/merchant-or-team-blocked/MerchantOrTeamBlockedInfoPanel';

enum JobPanelPages {
  DETAILS = 'details',
  EDIT_STATUS = 'edit-status',
  EDIT_STATUS_DETAILS = 'edit-status-details',
  EDIT_DESCRIPTION = 'edit-description',
  EDIT_LEAD_SOURCE = 'edit-lead-source',
  HISTORY = 'history'
}

const JobPanel: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);

  const [openJobAssigneesModal, setOpenJobAssigneesModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [suggestionList, setSuggestionList] = useState<AssignableMember[]>([]);
  const [currentPage, setCurrentPage] = useState(JobPanelPages.DETAILS);
  const [updatedStatus, setUpdatedStatus] = useState<JobStatus>();
  const [assigneeList, setAssigneeList] = useState([]);
  const [isRootState, setIsRootState] = useState(true);
  const { handleSidePanelClose, refetchJobsList, refetchEstimatesList } = useOutletContext<any>();
  const [job, setJob] = useState<Job>();
  const timezone = DateUtils.getTimezoneLongName();
  const [currentNote, setCurrentNote] = useState<JobTimelineEvent | null>(null);
  const [openDeleteNoteConfirmation, setOpenDeleteNoteConfirmation] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [showStatusSuccessModal, setShowStatusSuccessModal] = useState(false);
  const [showDeleteJobConfirmation, setShowDeleteJobConfirmation] = useState(false);
  const [notesInput, setNotesInput] = useState('');

  const [deleteJobMutation, { isLoading: isDeleteJobLoading }] = useDeleteJobMutation();

  const [editJobAssigneeMutation, editJobAssigneeState] = useEditJobAssigneesMutation();
  const { isLoading: isEditJobAssigneeLoading } = editJobAssigneeState;

  const [getJobTimelineQuery, getJobTimelineState] = useLazyGetJobTimelineQuery();
  const { data: jobTimelineResponse, isFetching: isJobTimelineFetching } = getJobTimelineState;
  const { timelineList } = jobTimelineResponse?.data ?? {};

  const { rootJobId, jobId, rootInvoiceId, rootReceiptId, rootPage, rootCustomerId, rootDisputeId, rootEstimateId } =
    useParams();

  const [getAssignableTeamMemberQuery, { data: assignableTeamMembers, isSuccess: assignableTeamMembersSuccess }] =
    useLazyGetAssignableMembersQuery();

  const [getJobByIdQuery, jobByIdState] = useLazyGetJobByIdQuery();
  const {
    data: jobData,
    isFetching: isJobByIdFetching,
    isSuccess: isJobByIdSuccess,
    isError: isJobByIdError
  } = jobByIdState;

  const [deleteNoteMutation, { isSuccess: isDeleteNoteSuccess, isLoading: isDeleteNoteLoading }] =
    useDeleteNoteMutation();

  const [editJobLeadSourceMutation, editJobLeadSourceState] = useEditJobLeadSourceMutation();
  const { isLoading: isEditJobLeadSourceLoading } = editJobLeadSourceState;

  const [editJobScheduleMutation, { isLoading: isEditJobScheduleLoading }] = useEditJobScheduleMutation();

  const getJobById = (jobIdParam: string) => {
    getJobByIdQuery({ jobId: jobIdParam, teamId: currentTeam.id });
  };

  useEffect(() => {
    if ((jobId || rootJobId) && currentTeam?.id) {
      getJobById(jobId ? jobId : rootJobId);
      getJobTimelineQuery({
        jobId: jobId ? jobId : rootJobId,
        teamId: currentTeam.id,
        timezone
      });
    }
  }, [jobId, rootJobId, currentTeam]);

  useEffect(() => {
    if (isJobByIdSuccess && !isJobByIdFetching) {
      setJob(jobData.data);
    }
  }, [isJobByIdSuccess, isJobByIdFetching]);

  useEffect(() => {
    if (!isDeleteNoteLoading && isDeleteNoteSuccess) {
      setCurrentNote(null);
      setOpenDeleteNoteConfirmation(false);
      refetchJobsTimeline();
    }
  }, [isDeleteNoteLoading, isDeleteNoteSuccess]);

  useEffect(() => {
    let routeStateTimeout;
    const routeState: SidePanelRouteState = location?.state ?? {};
    if (routeState?.fromSidePanel) {
      setIsRootState(false);
    }
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  useEffect(() => {
    if (currentTeam?.id) {
      getAssignableTeamMemberQuery(currentTeam?.id.toString());
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (assignableTeamMembersSuccess) {
      setSuggestionList(assignableTeamMembers.data.teamMemberList);
      if (job && !isJobByIdFetching) {
        const jobAssignees = job.assignees?.map(assignee => assignee.id);
        if (jobAssignees)
          setAssigneeList(
            assignableTeamMembers.data.teamMemberList.filter(member => jobAssignees.includes(member.merchantId))
          );
      }
    }
  }, [assignableTeamMembersSuccess, job, isJobByIdFetching]);

  const handleCloseAssignMembersModal = () => {
    setOpenJobAssigneesModal(false);
  };

  const hanldeEditAssigneesClick = () => {
    setOpenJobAssigneesModal(true);
  };

  const formatMemberList = (memberList: AssignableMember[]): Assignee[] => {
    return memberList.map(member => ({
      id: member.merchantId,
      name: member.name,
      profilePictureURL: member.profilePictureUrl
    }));
  };

  const handleSave = (membersList: AssignableMember[]) => {
    editJobAssigneeMutation({
      teamId: currentTeam.id,
      jobId: rootJobId || jobId,
      assignees: membersList.map(member => member.merchantId)
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setJob(prev => ({ ...prev, assignees: formatMemberList(membersList) }));
        if (refetchJobsList) {
          refetchJobsList();
        }
        setAssigneeList(membersList);
        setOpenJobAssigneesModal(false);
      }
    });
  };

  const getTeamMemberNames = () => {
    let remainingMemberCount = 0;
    if (assigneeList.length > 0) {
      let teamMemberNames = '';

      for (let index = 0; index < assigneeList.length; index++) {
        const nameToAdd = assigneeList[index].name;
        if (teamMemberNames.length < 27) {
          teamMemberNames += `${teamMemberNames.length > 0 ? ', ' : ''}${nameToAdd}`;
        } else {
          remainingMemberCount = assigneeList.length - index;
          break;
        }
      }

      return (
        <div className="flex items-center">
          <div className="max-w-[190px] overflow-hidden text-ellipsis whitespace-nowrap">
            {`${teamMemberNames.trim()}`}
          </div>
          {remainingMemberCount > 0 && <div className="ml-1 text-secondaryBtnText">{`+${remainingMemberCount}`}</div>}
        </div>
      );
    }
    return '';
  };

  const handleEditStatusClick = () => {
    setCurrentPage(JobPanelPages.EDIT_STATUS);
  };

  const handleStatusChange = (jobStatus: JobStatus) => {
    setUpdatedStatus(jobStatus);
    setCurrentPage(JobPanelPages.EDIT_STATUS_DETAILS);
  };

  const handleEditDetailsBackClick = () => {
    setUpdatedStatus(job.status);
    setCurrentPage(JobPanelPages.EDIT_STATUS);
  };

  const handleStatusSave = (jobStatus: JobStatus) => {
    if (refetchJobsList) {
      refetchJobsList();
    }
    setJob(prev => ({ ...prev, status: jobStatus }));
    setCurrentPage(JobPanelPages.DETAILS);
    if (jobStatus === JobStatus.DONE) {
      setShowStatusSuccessModal(true);
    }
  };

  const handleCustomerClick = () => {
    const rootState = {
      ...FROM_SIDE_PANEL_ROUTE_STATE,
      isFromJob: true
    };

    if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/customer`, {
        state: rootState
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    }
  };

  const handleEditDescriptionClick = () => {
    setCurrentPage(JobPanelPages.EDIT_DESCRIPTION);
  };

  const handleSaveDescriptionClick = (description: string) => {
    if (refetchJobsList) {
      refetchJobsList();
    }
    setJob(prev => ({ ...prev, description }));
    setCurrentPage(JobPanelPages.DETAILS);
  };

  const handleEditLeadSourceClick = () => {
    setCurrentPage(JobPanelPages.EDIT_LEAD_SOURCE);
    logAnalyticEvent(JobsCleverTapEvents.webJobsLeadSourcePageView);
  };

  const handleSaveLeadSourceClick = (leadSourceType: string, leadSource: string) => {
    return editJobLeadSourceMutation({
      leadSourceType,
      leadSource,
      jobId: rootJobId || jobId,
      teamId: currentTeam.id
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setJob(prev => ({ ...prev, leadSource, leadSourceType }));
        setCurrentPage(JobPanelPages.DETAILS);
        if (refetchJobsList) {
          refetchJobsList();
        }
      }
    });
  };

  const handleEditScheduleClick = () => {
    setOpenScheduleModal(true);
  };

  const handleSaveScheduleClick = (scheduledFrom: string, scheduledTo: string) => {
    editJobScheduleMutation({
      scheduledFrom,
      scheduledTo,
      jobId: rootJobId || jobId,
      teamId: currentTeam.id
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        if (refetchJobsList) {
          refetchJobsList();
        }
        setJob(prev => ({ ...prev, scheduledFrom, scheduledTo, isScheduled: true }));
        setOpenScheduleModal(false);
      }
    });
  };

  const handleViewHistoryClick = () => {
    setCurrentPage(JobPanelPages.HISTORY);
  };

  const refetchJobsTimeline = () => {
    getJobTimelineQuery({
      jobId: jobId ? jobId : rootJobId,
      teamId: currentTeam.id,
      timezone
    });
  };

  const handldeAddNoteClick = () => {
    setOpenNotesModal(true);
  };

  const handleEditNoteClick = (timelineEvent: JobTimelineEvent) => {
    setCurrentNote(timelineEvent);
    setOpenNotesModal(true);
  };

  const handleCloseNotesModalClick = () => {
    setCurrentNote(null);
    setOpenNotesModal(false);
  };

  const handleDeleteNoteClick = (timelineEvent: JobTimelineEvent) => {
    setCurrentNote(timelineEvent);
    setOpenDeleteNoteConfirmation(true);
  };

  const handleCloseDeleteNoteModal = () => {
    setCurrentNote(null);
    setOpenDeleteNoteConfirmation(false);
  };

  const onDeleteNoteConfrm = () => {
    deleteNoteMutation({
      teamId: currentTeam.id,
      jobId: rootJobId || jobId,
      noteId: currentNote?.noteId
    });
  };

  const showPreview = (index: number, note: JobTimelineEvent) => {
    setCurrentNote(note);
    setCurrentImage(index);
    setShowImageViewer(true);
    logAnalyticEvent(JobsCleverTapEvents.webImageViewerOpened);
  };

  const closePreview = () => {
    setCurrentNote(null);
    setCurrentImage(0);
    setShowImageViewer(false);
  };

  const handleInvoiceClick = (invoiceId: string) => {
    if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleAddorViewInvoice = () => {
    if (job.invoices?.length > 0) {
      handleInvoiceClick(job.invoices[0].id);
    } else {
      navigate(`/invoices/create?jobId=${job.id}`);
    }
    setShowStatusSuccessModal(false);
  };

  const onDeleteJob = () => {
    deleteJobMutation({ jobId: job.id, teamId: currentTeam.id });
    if (refetchJobsList) {
      refetchJobsList();
    }
    logAnalyticEvent(JobsCleverTapEvents.webDeleteJobConfirmation);
    navigate(-1);
  };

  const handleDeleteJob = () => {
    setShowDeleteJobConfirmation(true);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
        {isActionsBlocked ? (
          <MerchantOrTeamBlockedInfoPanel onBack={handleBackClick} />
        ) : (
          <>
            {currentPage === JobPanelPages.DETAILS && (
              <JobDetailsPanel
                onEditAssigneesClick={hanldeEditAssigneesClick}
                getTeamMemberNames={getTeamMemberNames}
                isRootState={isRootState}
                handleSidePanelClose={handleSidePanelClose}
                onEditStatusClick={handleEditStatusClick}
                job={job}
                handleCustomerClick={handleCustomerClick}
                isJobByIdFetching={isJobByIdFetching}
                onEditDescriptionClick={handleEditDescriptionClick}
                onEditLeadSourceClick={handleEditLeadSourceClick}
                onEditScheduleClick={handleEditScheduleClick}
                jobTimeline={timelineList}
                isJobTimelineFetching={isJobTimelineFetching}
                onViewHistoryClick={handleViewHistoryClick}
                onAddNoteClick={handldeAddNoteClick}
                onEditNoteClick={handleEditNoteClick}
                onDeleteNote={handleDeleteNoteClick}
                refetchJob={getJobById}
                handleInvoiceClick={handleInvoiceClick}
                handleStatusChange={handleStatusChange}
                handleDeleteJob={handleDeleteJob}
                isSingleMemberTeam={assignableTeamMembers?.data?.teamMemberList?.length === 1}
                isError={isJobByIdError}
                refetchEstimatesList={refetchEstimatesList}
              />
            )}
            {currentPage === JobPanelPages.EDIT_STATUS && (
              <EditStatusPanel
                status={job.status}
                onBackClick={() => setCurrentPage(JobPanelPages.DETAILS)}
                handleStatusChange={handleStatusChange}
              />
            )}
            {currentPage === JobPanelPages.EDIT_STATUS_DETAILS && (
              <EditStatusDetailPanel
                status={updatedStatus}
                onBackClick={handleEditDetailsBackClick}
                onSave={handleStatusSave}
                jobId={rootJobId || jobId}
                teamId={currentTeam.id}
              />
            )}
            {currentPage === JobPanelPages.EDIT_DESCRIPTION && (
              <EditDescriptionPanel
                description={job.description}
                onBackClick={() => setCurrentPage(JobPanelPages.DETAILS)}
                onSave={handleSaveDescriptionClick}
                jobId={rootJobId || jobId}
                teamId={currentTeam.id}
              />
            )}
            {currentPage === JobPanelPages.EDIT_LEAD_SOURCE && (
              <EditLeadSourcePanel
                leadSource={job.leadSource}
                leadSourceType={job.leadSourceType}
                onBackClick={() => setCurrentPage(JobPanelPages.DETAILS)}
                onSaveClick={handleSaveLeadSourceClick}
                isActionInProgress={isEditJobLeadSourceLoading}
              />
            )}
            {currentPage === JobPanelPages.HISTORY && (
              <JobHistoryPanel
                jobTimeline={timelineList}
                onBackClick={() => setCurrentPage(JobPanelPages.DETAILS)}
                jobId={rootJobId || jobId}
                teamId={currentTeam.id}
                getJobTimeline={refetchJobsTimeline}
                onEditNote={handleEditNoteClick}
                onAddNote={handldeAddNoteClick}
                onDeleteNote={handleDeleteNoteClick}
                showPreview={showPreview}
                notesInput={notesInput}
                setNotesInput={setNotesInput}
              />
            )}
          </>
        )}
      </SidePanel>

      {openJobAssigneesModal && (
        <AssignableMembersModal
          list={suggestionList}
          onClose={handleCloseAssignMembersModal}
          selectedMembers={assigneeList}
          onSave={handleSave}
          isLoading={isEditJobAssigneeLoading}
        />
      )}

      {openScheduleModal && (
        <EditScheduleModal
          onClose={() => setOpenScheduleModal(false)}
          onSave={handleSaveScheduleClick}
          scheduledFrom={job.scheduledFrom}
          scheduledTo={job.scheduledTo}
          isScheduled={job.isScheduled}
          isLoading={isEditJobScheduleLoading}
        />
      )}

      {openNotesModal && (
        <CreateEditNoteModal
          onClose={handleCloseNotesModalClick}
          isEdit={!!currentNote}
          currentNote={currentNote}
          teamId={currentTeam.id}
          jobId={rootJobId || jobId}
          getJobTimeline={refetchJobsTimeline}
          defaultNote={notesInput}
          setNotesInput={setNotesInput}
        />
      )}
      {openDeleteNoteConfirmation && (
        <DeleteNoteConfirmationModal
          handleDelete={onDeleteNoteConfrm}
          onCloseClick={handleCloseDeleteNoteModal}
          processingDelete={isDeleteNoteLoading}
        />
      )}
      {showImageViewer && (
        <ImagePreview onClose={closePreview} images={currentNote?.attachments} selectedIndex={currentImage} />
      )}

      {showStatusSuccessModal && (
        <DoneStatusSuccessModal
          onAddInvoiceClick={handleAddorViewInvoice}
          invoice={job?.invoices?.length > 0 ? job.invoices[0] : null}
          onClose={() => setShowStatusSuccessModal(false)}
        />
      )}
      {showDeleteJobConfirmation && (
        <DeleteJobConfirmationModal
          onCloseClick={() => setShowDeleteJobConfirmation(false)}
          handleDelete={onDeleteJob}
          processingDelete={isDeleteJobLoading}
        />
      )}
    </>
  );
};

export default JobPanel;
